import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Landmark, LogOut, Loader2, CheckCircle, HandHeart, ChevronRight, EyeOff, Pencil, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleAvatar } from "@/components/CircleAvatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, normFeed, clockTime, platformFee } from "@/lib/blessed";
import PayoutMethods from "@/components/PayoutMethods";

const BADGES = [
  { emoji: "🌱", label: "First blessing", need: (s) => s.given >= 1 },
  { emoji: "🌿", label: "10 blessings", need: (s) => s.given >= 10 },
  { emoji: "🥦", label: "50 blessings", need: (s) => s.given >= 50 },
  { emoji: "💯", label: "$100 total", need: (s) => s.amount >= 100 },
  { emoji: "🏆", label: "$500 total", need: (s) => s.amount >= 500 },
  { emoji: "🔥", label: "7-day streak", need: (s) => s.streak >= 7 },
  { emoji: "⚡", label: "30-day streak", need: (s) => s.streak >= 30 },
  { emoji: "🎉", label: "Fundraiser", need: (s) => s.fundraiser },
  { emoji: "💜", label: "Kind soul", need: (s) => s.given >= 100 },
];

const Stat = ({ value, label, prefix }) => (
  <div className="flex-1 text-center px-2">
    <div className="text-3xl font-black">{prefix}{value}</div>
    <div className="text-sm font-bold text-ink-tertiary mt-0.5">{label}</div>
  </div>
);

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [myDonations, setMyDonations] = useState([]);
  const [feedReceived, setFeedReceived] = useState({ net: 0, gross: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [payingOut, setPayingOut] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [savingHidden, setSavingHidden] = useState(false);
  const [recipientStory, setRecipientStory] = useState("");
  const [bioModal, setBioModal] = useState(null); // { value } when open
  const [savingBio, setSavingBio] = useState(false);

  const isRecipient = !!user?.recipient_id;
  const bioText = user?.bio || recipientStory || "";

  useEffect(() => {
    if (!isRecipient) return;
    api.get("/recipients/me").then((r) => {
      setHidden(!!r.data?.hidden);
      setRecipientStory(r.data?.story || "");
    }).catch(() => {});
  }, [isRecipient]);

  const saveBio = async () => {
    const trimmed = (bioModal?.value || "").trim().slice(0, 240);
    setSavingBio(true);
    try {
      const { data } = await api.put("/users/me/bio", { bio: trimmed });
      setRecipientStory(data?.bio || trimmed);
      await refreshUser();
      if (isRecipient) {
        api.get("/recipients/me").then((r) => setRecipientStory(r.data?.story || "")).catch(() => {});
      }
      toast.success("About me updated 💛");
      setBioModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSavingBio(false);
    }
  };

  const toggleHidden = async (next) => {
    const prev = hidden;
    setHidden(next); // optimistic
    setSavingHidden(true);
    try {
      const { data } = await api.put("/recipients/me/visibility", { hidden: next });
      if (data && typeof data.hidden === "boolean") setHidden(data.hidden);
      toast.success(next ? "You're now hidden from the map." : "You're visible on the map again.");
    } catch (e) {
      setHidden(prev); // rollback
      toast.error("Couldn't update visibility. Try again.");
    } finally {
      setSavingHidden(false);
    }
  };

  useEffect(() => {
    (async () => {
      setStats(await api.get("/users/me/stats").then((r) => r.data).catch(() => ({})));
      if (isRecipient) {
        setBalance(await api.get("/recipients/me/balance").then((r) => r.data).catch(() => null));
        // Derive "received" straight from the live feed (same source the live chat uses),
        // so the Profile reflects real gifts even when Stripe's Connect balance lags.
        const feed = await api.get("/feed?limit=200").then((r) => r.data).catch(() => []);
        const items = (Array.isArray(feed) ? feed : feed.items || feed.feed || []).map(normFeed);
        const since = Date.now() - 4 * 86400 * 1000;
        const mine = items.filter(
          (f) =>
            Number(f.amount) > 0 &&
            (f.recipientId === user.recipient_id || f.isMineAsRecipient) &&
            (!f.createdAt || new Date(f.createdAt).getTime() >= since)
        );
        const gross = mine.reduce((s, f) => s + Number(f.amount || 0), 0);
        const net = mine.reduce((s, f) => s + Math.max(0, Number(f.amount || 0) - platformFee(Number(f.amount || 0))), 0);
        setFeedReceived({ net, gross, count: mine.length });
      }
      const dons = await api.get("/donations/me").then((r) => r.data).catch(() => []);
      const list = Array.isArray(dons) ? dons : dons.items || dons.donations || [];
      setMyDonations(list.map(normFeed));
      setLoading(false);
    })();
  }, [isRecipient]);

  const given = stats?.gifts_given ?? stats?.blessings_sent ?? stats?.given_count ?? stats?.donations_given ?? 0;
  const amount = stats?.given_total ?? stats?.total_given ?? (stats?.total_given_cents != null ? stats.total_given_cents / 100 : 0);
  const streak = stats?.streak ?? stats?.streak_days ?? 0;
  const sctx = { given, amount, streak, fundraiser: !!stats?.has_fundraiser };
  const unlocked = BADGES.filter((b) => b.need(sctx)).length;

  const cents = (v) => (typeof v === "number" ? v / 100 : 0);
  const availBal =
    balance?.available_cents != null ? cents(balance.available_cents)
    : balance?.available != null ? balance.available
    : balance?.balance_cents != null ? cents(balance.balance_cents)
    : balance?.balance ?? 0;
  const pendingBal =
    balance?.pending_cents != null ? cents(balance.pending_cents)
    : balance?.pending != null ? balance.pending
    : balance?.incoming_cents != null ? cents(balance.incoming_cents)
    : balance?.incoming != null ? balance.incoming
    : balance?.upcoming_cents != null ? cents(balance.upcoming_cents)
    : 0;

  // Received totals. Prefer whatever is largest across: Stripe balance, backend DB total,
  // and the live-feed-derived total (the source the live chat uses).
  const dbNet = cents(balance?.total_received_net_cents);
  const dbGross = cents(balance?.total_received_cents);
  const stripeTotal = availBal + pendingBal;
  const transitNet = Math.max(dbNet, feedReceived.net);
  const transitCount = Math.max(balance?.total_received_count || 0, feedReceived.count);
  const transitGross = Math.max(dbGross, feedReceived.gross);
  const balanceDisplay = Math.max(stripeTotal, transitNet);

  const payout = async () => {
    setPayingOut(true);
    try {
      await api.post("/recipients/me/payout", { amount_cents: Math.round(availBal * 100), method: "standard" });
      toast.success("Payout requested! 💸");
      setBalance(await api.get("/recipients/me/balance").then((r) => r.data).catch(() => null));
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout unavailable."); }
    finally { setPayingOut(false); }
  };

  const stripeLink = async (path) => {
    try {
      const back = `${window.location.origin}${window.location.pathname}`;
      const payload = { return_url: back, refresh_url: back, returnUrl: back, refreshUrl: back };
      const { data } = await api.post(path, payload);
      const url = data?.url || data?.link || data?.dashboard_url || data?.upstream_location;
      if (url) window.location.href = url;
      else toast.error("Link unavailable.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `Stripe: ${detail}` : `Could not open Stripe (${e?.response?.status || "network error"}).`);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      <div className="flex items-center gap-4 mb-6">
        <CircleAvatar src={user?.picture} name={user?.name} size={72} ring="linear-gradient(135deg,#1E40AF,#8B5CF6)" />
        <div className="min-w-0">
          <h1 className="text-2xl font-black truncate">{user?.name}</h1>
          <p className="text-sm text-ink-tertiary font-semibold truncate">{user?.email}</p>
        </div>
      </div>

      {/* About me — writes to users.bio and mirrors to recipient.story (map pin + donate page) */}
      <button data-testid="about-me-card" onClick={() => setBioModal({ value: bioText })}
        className="w-full text-left bg-surface-2 border border-border rounded-md p-4 mb-5 hover:bg-surface-3 transition-colors">
        <div className="flex items-center justify-between mb-1.5">
          <span className="flex items-center gap-2 font-extrabold"><UserCircle size={18} className="text-brand-primary" /> About me</span>
          <span data-testid="about-me-action" className="flex items-center gap-1 text-xs font-black text-brand-primary">
            {bioText ? <><Pencil size={12} /> Edit</> : "+ Add"}
          </span>
        </div>
        <p data-testid="about-me-text" className={`text-sm leading-relaxed ${bioText ? "text-ink" : "italic text-ink-tertiary"}`}>
          {bioText || "Tell neighbors a little about yourself — max 240 characters. Shown on your profile and on your map pin."}
        </p>
      </button>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-brand-primary" size={28} /></div>
      ) : (
        <>
          {/* Link-your-bank prompt for recipients who haven't finished Stripe onboarding */}
          {isRecipient && balance && balance.payouts_enabled === false && (
            <button data-testid="link-bank-banner" onClick={() => navigate("/onboard-bank")}
              className="w-full flex items-center gap-3 rounded-lg p-4 mb-5 text-left text-white transition-transform hover:scale-[1.01]"
              style={{ background: "linear-gradient(135deg,#FFD93D,#FF8A3D)" }}>
              <div className="w-10 h-10 rounded-pill grid place-items-center bg-white/25 shrink-0"><Landmark size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-black" style={{ color: "#1B1233" }}>Link your bank to get paid</div>
                <div className="text-xs font-bold" style={{ color: "#1B1233CC" }}>Blessings are waiting — connect a bank or debit card to receive your money.</div>
              </div>
              <ChevronRight size={20} style={{ color: "#1B1233" }} />
            </button>
          )}

          {/* Balance */}
          {isRecipient && (
            <div className="rounded-lg p-6 mb-5 text-white" style={{ background: "#1E40AF" }}>
              <div className="flex items-center gap-2 text-white/85 text-sm font-black uppercase tracking-wide"><Landmark size={16} /> Your balance</div>
              <div className="text-5xl font-black mt-1">{money(balanceDisplay)}</div>
              {(balanceDisplay > 0 || transitGross > 0) && (
                <div data-testid="received-note" className="text-sm text-white/90 font-black mt-1">
                  💛 Blessings received — arriving in your bank in ~2 business days
                </div>
              )}
              {pendingBal > 0 && (
                <div data-testid="pending-balance" className="text-xs text-white/75 font-bold mt-1">
                  {money(availBal)} available · {money(pendingBal)} pending
                </div>
              )}
              {transitCount > 0 && transitNet > stripeTotal && (
                <div data-testid="in-transit" className="text-xs text-white/75 font-bold mt-1">
                  {transitCount} gift{transitCount === 1 ? "" : "s"} received · {money(transitNet)} total (in transit)
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button data-testid="withdraw-button" disabled={!availBal || payingOut} onClick={payout}
                  className="flex-1 h-12 rounded-pill font-black bg-white/25 disabled:opacity-70">
                  {payingOut ? "…" : availBal ? `Withdraw ${money(availBal)}` : "Nothing to withdraw yet"}
                </button>
                <button data-testid="manage-payout" onClick={() => navigate("/onboard-bank")} className="px-6 h-12 rounded-pill font-black border border-white/40">Manage</button>
              </div>
              <button data-testid="change-bank" onClick={() => stripeLink("/recipients/me/stripe-payout-details-link")} className="w-full text-center font-black mt-5 border-t border-white/20 pt-4">
                Change bank or debit card →
              </button>
              <p className="text-sm text-white/80 mt-3 leading-snug">Bank transfers arrive in ~2 business days. Tap Change bank/card to open a Stripe-hosted form where you can remove your current payout method and add a new one.</p>
            </div>
          )}

          {isRecipient && <PayoutMethods recipientId={user.recipient_id} />}

          {/* Recipient status / become */}
          {isRecipient ? (
            <div className="flex items-center gap-3 bg-surface-2 border border-border rounded-md p-4 mb-5">
              <CheckCircle className="text-successb" size={24} />
              <div className="font-black">You're listed as a recipient</div>
            </div>
          ) : (
            <button data-testid="become-recipient-link" onClick={() => navigate("/become-recipient")}
              className="w-full flex items-center gap-3 bg-surface-2 border border-border rounded-md p-4 mb-5 hover:bg-surface-3 transition-colors">
              <div className="w-10 h-10 rounded-pill grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#FFD93D,#FF8A3D)" }}><HandHeart size={20} /></div>
              <div className="flex-1 text-left"><div className="font-extrabold">Become a recipient</div><div className="text-xs text-ink-tertiary">Share your story to receive blessings</div></div>
              <ChevronRight className="text-ink-tertiary" size={20} />
            </button>
          )}
        </>
      )}

      {/* Hide me from the map — recipients opt out of appearing to donors */}
      <div data-testid="hide-profile-row" className="flex items-center gap-3 bg-surface-2 border border-border rounded-md p-4 mb-4">
        <div className="w-10 h-10 rounded-pill grid place-items-center text-ink-tertiary bg-surface-3 shrink-0"><EyeOff size={20} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold">Hide me from the map</div>
          <div className="text-xs text-ink-tertiary">
            {!isRecipient
              ? "You're not on the map yet — you only appear once you share a story."
              : hidden
                ? "You're invisible to donors. You can still send gifts."
                : "You're visible to donors within 30 days of activity."}
          </div>
        </div>
        <Switch data-testid="hide-profile-toggle" checked={hidden} disabled={!isRecipient || savingHidden} onCheckedChange={toggleHidden} />
      </div>

      <Button data-testid="signout-button" variant="outline" onClick={logout} className="w-full h-12 rounded-pill font-extrabold border-border text-errorb">
        <LogOut size={18} className="mr-1" /> Sign out
      </Button>

      {/* Edit About me modal */}
      <Dialog open={!!bioModal} onOpenChange={(o) => !o && setBioModal(null)}>
        <DialogContent className="bg-surface-2 border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <UserCircle size={20} className="text-brand-primary" /> About me
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-tertiary font-semibold -mt-1">
            Shown on your profile and on your map pin so neighbors know a little about you.
          </p>
          <Textarea
            data-testid="about-me-input"
            rows={4}
            maxLength={240}
            autoFocus
            value={bioModal?.value || ""}
            onChange={(e) => setBioModal({ value: e.target.value })}
            placeholder="Neighbor who loves helping people out."
            className="resize-none bg-surface border-border text-ink"
          />
          <div className="flex items-center justify-between">
            <span data-testid="about-me-counter" className="text-xs font-bold text-ink-tertiary">{(bioModal?.value || "").length}/240</span>
            <div className="flex gap-2">
              <Button data-testid="about-me-cancel" variant="outline" onClick={() => setBioModal(null)} className="rounded-pill font-black border-border">Cancel</Button>
              <Button data-testid="about-me-save" disabled={savingBio} onClick={saveBio} className="rounded-pill font-black bg-brand-primary text-white">
                {savingBio ? <Loader2 className="animate-spin" size={18} /> : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
